
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Clock, Bell } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface ComingSoonFeatureProps {
  featureName: string;
}

const ComingSoonFeature: React.FC<ComingSoonFeatureProps> = ({ featureName }) => {
  const { toast } = useToast();

  const getFeatureTitle = (name: string) => {
    const titles: { [key: string]: string } = {
      teenFellowshipPrograms: 'Teen-Only Fellowship Programs',
      monthlyMentorHours: '1:1 Monthly Mentor Power Hours',
      shadowFounderExperience: 'Shadow a Founder – Virtual Experience',
      verifiedSkillPortfolio: 'Verified Skill Portfolio Builder',
      communityLeaderBadge: 'Community Leader Badge & Recognition',
      earlyAccessEvents: 'Early Access to Internships, Contests & Events',
      microInternshipProgram: 'Host a Micro-Internship Program',
      startupNewsroomFeature: 'Startup Feature on Teenvisor Newsroom',
      teenCommunityBuilding: 'Teen Community Building Suite',
      courseCreatorAccess: 'Course & Challenge Creator Access',
      talentMatchVerified: 'Talent Match with Verified Teens',
      premiumOpportunityPosting: 'Post Premium Opportunities & Gigs',
      startupStoriesPublishing: 'Startup Stories – Founders\' Series Publishing',
      inviteOnlyRoundtables: 'Invite-Only Roundtables with Teen Innovators',
      analyticsEngagement: 'Analytics Dashboard for Community Engagement',
      teenMarketTesting: 'Teen Market Testing & Survey Tools'
    };
    return titles[name] || 'Premium Feature';
  };

  const notifyWhenReady = () => {
    toast({
      title: "Notification Set!",
      description: "We'll notify you when this feature becomes available."
    });
  };

  return (
    <Card className="max-w-2xl mx-auto">
      <CardHeader className="text-center">
        <CardTitle className="flex items-center justify-center gap-2">
          <Clock className="h-6 w-6 text-orange-500" />
          {getFeatureTitle(featureName)}
        </CardTitle>
        <CardDescription>
          This premium feature is currently in development and will be available soon.
        </CardDescription>
      </CardHeader>
      <CardContent className="text-center space-y-6">
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
          <h3 className="font-semibold text-orange-900 mb-2">Coming Soon!</h3>
          <p className="text-orange-700 text-sm">
            We're working hard to bring you this amazing feature. As a premium member, 
            you'll get early access as soon as it's ready.
          </p>
        </div>
        
        <div className="space-y-4">
          <h4 className="font-medium">What to expect:</h4>
          <ul className="text-sm text-muted-foreground space-y-2">
            <li>• Full feature functionality</li>
            <li>• Premium-exclusive access</li>
            <li>• Enhanced user experience</li>
            <li>• Regular updates and improvements</li>
          </ul>
        </div>

        <Button 
          onClick={notifyWhenReady}
          className="bg-orange-500 hover:bg-orange-600"
        >
          <Bell className="h-4 w-4 mr-2" />
          Notify Me When Ready
        </Button>

        <p className="text-xs text-muted-foreground">
          Estimated release: Q2 2024
        </p>
      </CardContent>
    </Card>
  );
};

export default ComingSoonFeature;
